import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Button,
    CircularProgress,
    Divider
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import ArticleIcon from '@mui/icons-material/Article';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import axios from '../../utils/axios';

const Dashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState({
        recentExams: [],
        activeSubscriptions: [],
        examHistory: [],
        readingHistory: []
    });

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [examsRes, subscriptionsRes, historyRes] = await Promise.all([
                    axios.get('/api/exams/user/recent'),
                    axios.get('/api/subscriptions/user'),
                    axios.get('/api/exams/user/history')
                ]);
                console.log(subscriptionsRes);
                setDashboardData({
                    recentExams: examsRes.data,
                    activeSubscriptions: subscriptionsRes.data.subscriptions,
                    examHistory: historyRes.data,
                    readingHistory: [] // To be implemented when article history is added
                });
            } catch (err) {
                setError(err.response?.data?.message || 'Error fetching dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container>
                <Typography color="error" align="center">
                    {error}
                </Typography>
            </Container>
        );
    }

    const renderEmptyState = (message) => (
        <ListItem>
            <ListItemText primary={message} />
        </ListItem>
    );

    return (
        <Container sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom>
                Welcome to Your Dashboard
            </Typography>

            <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
                {/* View Exams */}
                <Grid item xs={12} sm={6} md={4}>
                    <Paper sx={{ p: 2, height: '100%', width: '250px' }}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <QuizIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6">My Exams</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                                onClick={() => navigate('/my-exams')}
                            >
                                My Exams
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* Exam History */}
                <Grid item xs={12} sm={6} md={4}>
                    <Paper sx={{ p: 2, height: '100%', width: '250px' }}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <SchoolIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6">Exam History</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Button 
                                variant="outlined" 
                                color="primary"
                                fullWidth
                                onClick={() => navigate('/exams/history')}
                            >
                                View Exam Results
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Active Subscriptions List */}
            <Box sx={{ mt: 4 }}>
                <Typography variant="h5" gutterBottom>
                    Your Active Subscriptions
                </Typography>
                <Paper sx={{ p: 2 }}>
                    {dashboardData.activeSubscriptions.length > 0 ? (
                        <>
                            <List>
                                {dashboardData.activeSubscriptions.slice(0, 3).map((subscription) => (
                                    <ListItem key={subscription._id}>
                                        <ListItemIcon>
                                            <CardMembershipIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={subscription.exam?.title || 'Unknown Exam'} 
                                            secondary={`Expires: ${new Date(subscription.endDate).toLocaleDateString()}`}
                                        />
                                        <Button 
                                            variant="outlined" 
                                            size="small"
                                            onClick={() => navigate(`/exams/${subscription.exam?._id}`)}
                                        >
                                            View
                                        </Button>
                                    </ListItem>
                                ))}
                            </List>
                            {dashboardData.activeSubscriptions.length > 3 && (
                                <Box sx={{ textAlign: 'center', mt: 2 }}>
                                    <Button 
                                        variant="outlined" 
                                        onClick={() => navigate('/my-exams')}
                                    >
                                        View All Subscriptions ({dashboardData.activeSubscriptions.length})
                                    </Button>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="body1" color="text.secondary" gutterBottom>
                                You don't have any active subscriptions.
                            </Typography>
                            <Button 
                                variant="contained" 
                                color="primary"
                                onClick={() => navigate('/exams')}
                                sx={{ mt: 1 }}
                            >
                                Browse Exams
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};

export default Dashboard; 